import { Injectable, Inject, inject } from '@angular/core';


import {
	HttpInterceptor, HttpRequest, HttpHandler, HttpEvent,
	HttpResponse, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ContentService } from '../services/content.service';
import { PreviousRouteService } from '../services/previous-route-service';
import { UAParser } from 'ua-parser-js';

function randomString(len: number) {
	const chars = 'ABCDEFGHJKLMNOPRSTUVWXYZ23456789';
	let out = '';
	for (let i = 0; i < len; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
	return out;
}

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
	private snack = inject(MatSnackBar);
	private router = inject(Router);
	private content = inject(ContentService);
	private prevRoute = inject(PreviousRouteService);

	constructor(
	) { }

	private static readonly LOGIN_PATHS = ['/Login', '/LoginGlobal'];
	private static readonly POST_NAV_WINDOW_MS = 3000;


	private shouldIgnorePostLogin401(failingUrl: string | null): boolean {
		const prev = this.prevRoute.previousUrl || '';
		const curr = this.prevRoute.currentUrl || '';
		const justNavigated =
			Date.now() - this.prevRoute.lastNavAt < AppHttpInterceptor.POST_NAV_WINDOW_MS;

		const cameFromLogin = AppHttpInterceptor.LOGIN_PATHS.some(p => prev.includes(p));
		const landedOnMainOverview = /\/Main\/MainOverview(?:$|[?#])/i.test(curr);

		// Optional: also ensure it's your API (not assets) and the server URL matches
		const isApiCall = !!failingUrl && /\/api\//i.test(failingUrl);

		return !!failingUrl && isApiCall && justNavigated && cameFromLogin && landedOnMainOverview;
	}
	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// ----- REQUEST: add headers like the AngularJS interceptor -----
		const tenant = localStorage.getItem('tenant') || undefined;

		let clientTraceId = localStorage.getItem('clientTraceId');
		if (!clientTraceId) {
			clientTraceId = 'CT-' + randomString(6);
			localStorage.setItem('clientTraceId', clientTraceId);
		}

		let clientInfo = localStorage.getItem('clientInfo');
		if (!clientInfo) {
			const p = new UAParser(navigator.userAgent);
			clientInfo = JSON.stringify({
				device: p.getDevice(), os: p.getOS(), browser: p.getBrowser()
			});
			localStorage.setItem('clientInfo', clientInfo);
		}

		const augmented = req.clone({
			setHeaders: {
				...(tenant ? { 'X-Global-Tenant': tenant } : {}),
				'X-Client-TraceId': clientTraceId,
				'X-Client-Info': clientInfo
			}
		});

		return next.handle(augmented).pipe(
			// ----- RESPONSE: copy the old logic -----
			tap((evt) => {
				if (evt instanceof HttpResponse) {
					// mirror: store X-Global-Tenant header if present
					const newTenant = evt.headers.get('X-Global-Tenant');
					if (newTenant) localStorage.setItem('tenant', newTenant);

					// body-level errorMessage like old code
					const body = evt.body;
					if (body && body.errorMessage) {
						this.snack.open(body.errorMessage, undefined, { horizontalPosition: 'left', verticalPosition: 'top' });
						// throw to caller like $q.reject
						throw body.errorMessage;
					}
				}
			}),
			catchError((err: HttpErrorResponse) => {

				if (err.status === 401 && this.shouldIgnorePostLogin401(err.url)) {
					// If you want to completely swallow the error for this one call, return EMPTY.
					// return EMPTY;

					// Otherwise: skip dev window/toast but still propagate to caller:
					return throwError(() => err);
				}

				const resp = err;

				const error = resp?.error?.error || resp?.error;

				// TenantUndefined → redirect like before
				if (error?.ErrorCode === 'TenantUndefined') {
					// If you need PermissionService.isGlobalUser(), use Angular version or gate this.
					// Assuming global users need redirect:
					this.router.navigate(['/Global/SetCurrentTenant']);
					return throwError(() => err);
				}

				// Non-expired-token errors: dev helper window & toast
				if (error?.ExceptionName !== 'SecurityTokenExpiredException') {
					const isLocalhost = (window as any).SdmGlobal?.getEnv?.() === 'd0';
					if (isLocalhost) {
						const dump = JSON.stringify(resp, null, 2);
						const w = window.open('', 'sdmErrorWindow')!;
						w.document.close();
						w.document.write(`<html><head><title>SDM Error</title></head><body><pre>${dump}</pre></body></html>`);
					}

					let msg = 'Unknown error, please contact your system administrator.';
					if (error?.ErrorCode) {
						const t = this.content.getContent('SdmException.' + error.ErrorCode);
						msg = t && t !== '-' ? t : this.content.getContent('Standard.UnknownError', error?.TraceId, error?.ErrorCode);
					}

					// Simple toast replacement for the old $mdToast template
					this.snack.open(msg, this.content.getContent('Standard.Dismiss') || 'Dismiss', {
						duration: 10000,
						horizontalPosition: 'left',
						verticalPosition: 'bottom'
					});
				}

				return throwError(() => err);
			})
		);
	}
}
